import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Link2, LayoutList, Newspaper, GraduationCap, HelpCircle,
  Megaphone, MessageSquareQuote, Image, Search, Settings, Users as UsersIcon,
  Inbox, ScrollText, LogOut, Menu, X, ExternalLink, Images, Youtube, CalendarClock, ListTree, Lightbulb, FileText,
} from "lucide-react";
import Logo from "@/components/public/Logo";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/perms";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", Icon: LayoutDashboard, res: "dashboard" },
  { to: "/admin/links", label: "Central Link Manager", Icon: Link2, res: "links" },
  { to: "/admin/menu", label: "Menu Manager", Icon: ListTree, res: "menu" },
  { to: "/admin/sections", label: "Page & Sections", Icon: LayoutList, res: "sections" },
  { to: "/admin/pages", label: "Custom Pages", Icon: FileText, res: "pages" },
  { to: "/admin/hero-slides", label: "Hero Slides", Icon: Images, res: "hero_slides" },
  { to: "/admin/news", label: "News & Analysis", Icon: Newspaper, res: "news" },
  { to: "/admin/trading-ideas", label: "Trading Ideas", Icon: Lightbulb, res: "trading_ideas" },
  { to: "/admin/education", label: "Education Center", Icon: GraduationCap, res: "education" },
  { to: "/admin/videos", label: "Latest Videos", Icon: Youtube, res: "videos" },
  { to: "/admin/events", label: "Economic Calendar", Icon: CalendarClock, res: "events" },
  { to: "/admin/faq", label: "FAQ Manager", Icon: HelpCircle, res: "faq" },
  { to: "/admin/promotions", label: "Promotions", Icon: Megaphone, res: "promotions" },
  { to: "/admin/testimonials", label: "Testimonials", Icon: MessageSquareQuote, res: "testimonials" },
  { to: "/admin/media", label: "Media Library", Icon: Image, res: "media" },
  { to: "/admin/seo", label: "SEO Manager", Icon: Search, res: "seo" },
  { to: "/admin/settings", label: "Site Settings", Icon: Settings, res: "settings" },
  { to: "/admin/contacts", label: "Contact Inbox", Icon: Inbox, res: "contacts" },
  { to: "/admin/users", label: "User Management", Icon: UsersIcon, res: "users" },
  { to: "/admin/audit", label: "Audit Log", Icon: ScrollText, res: "audit" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => canAccess(user?.role, n.res));

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#06122E] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#081734] border-r border-[#203E70] flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center px-5 border-b border-[#203E70]">
          <Link to="/admin"><Logo size="sm" /></Link>
        </div>
        <nav className="flex-1 overflow-y-auto admin-scroll py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${isActive ? "bg-[#102653] text-[#D8AE4E]" : "text-slate-300 hover:bg-[#102653]/60 hover:text-white"}`}
              data-testid={`nav-${n.res}`}
            >
              <n.Icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#203E70]">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded text-sm text-slate-300 hover:text-[#D8AE4E] transition-colors" data-testid="view-site-link">
            <ExternalLink size={17} /> View Website
          </a>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-[#081734] border-b border-[#203E70] flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="admin-sidebar-toggle">{open ? <X /> : <Menu />}</button>
          <div className="flex items-center gap-4 ml-auto">
            <div className="text-right">
              <p className="text-sm text-white leading-tight">{user?.name}</p>
              <p className="text-xs gold-text">{user?.role_label}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#102653] border border-[#203E70] flex items-center justify-center text-[#D8AE4E] font-semibold text-sm">
              {(user?.name || "A").charAt(0).toUpperCase()}
            </div>
            <button onClick={doLogout} className="btn-outline-gold px-3 py-2 text-sm inline-flex items-center gap-1.5" data-testid="admin-logout"><LogOut size={15} /> Logout</button>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto admin-scroll">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
